import '../../scss/pages/_wheel.scss';

import dayjs from 'dayjs';
import { setTimeout } from 'isomorphic-timers-promises';
import _, { findIndex, map, sample } from 'lodash';
import { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useToggle } from 'react-use';
import { useConfig } from '~components/hooks';
import { Loader } from '~components/Loader';
import { useOpenModal } from '~components/modals/hooks';
import { CircleControls, Prizes } from '~components/wheel';
import { usePrizes } from '~components/wheel/hooks';
import playAudio from '~frontend/utils/playAudio';
import { useHttpClient } from '~shared/frontend/providers/HttpProvider';
import {
  Config as ConfigType,
  Profile as ProfileType,
  WheelGame as WheelGameType,
} from '~types';

import { gql, useMutation, useQuery } from '@apollo/client';

interface WheelData {
  getConfig: ConfigType;
  getProfile: ProfileType;
}

interface WheelMutationData {
  signWheel: any & WheelGameType;
}

interface WheelMutationVars {
  input: {
    ids: number[];
  };
}

let audioInterval = null;
let abortController = null;

const Wheel: FunctionComponent = () => {
  const { setHttpContext } = useHttpClient();
  const [cookies, setCookie] = useCookies(['wheelModal', 'redirect']);
  const { onShow } = useOpenModal();
  const getConfig = useConfig();
  const [isActive, setActive] = useToggle(false);
  const navigate = useNavigate();
  const isMobile =
    getConfig?.userAgent.match(
      /Android|BlackBerry|iPhone|iPod|Opera Mini|IEMobile|WPDesktop/i,
    ) ?? false;

  const [style, setStyle] = useState({});

  useEffect(() => {
    abortController = new AbortController();

    return () => {
      clearInterval(audioInterval);
      abortController.abort();
    };
  }, []);

  const [prizes] = usePrizes();

  const { t } = useTranslation();

  const { loading, error, data, refetch } = useQuery<WheelData>(
    gql`
      query Wheel {
        getProfile {
          id
          getWheelLetters
          getWheelStep
          lastWheelGame
        }
      }
    `,
  );

  const [signWheel] = useMutation<WheelMutationData, WheelMutationVars>(
    gql`
      mutation SignWheel($input: SignWheelInput!) {
        signWheel(input: $input) {
          id
          prizeId
          type
          value
        }
      }
    `,
  );

  if (loading) {
    return <Loader />;
  } else if (error) {
    return <>Wheel | Error! {error.message}</>;
  }

  const getProfile = data?.getProfile;

  const onSignWheel = async (event: SyntheticEvent<HTMLDivElement>) => {
    try {
      if (event) {
        event.preventDefault();
      }

      if (isActive) {
        return null;
      }

      setActive(true);

      if (!getProfile?.getWheelStep) {
        throw Error('wheel_autorization_error');
      } else if (getProfile?.getWheelStep === 1) {
        setCookie('redirect', '/wheel', {
          expires: dayjs().add(5, 'minutes').toDate(),
        });

        return document.getElementById('vk_login').click();
      }

      window?.dataLayer?.push({
        event: 'run_wheel',
      });

      const {
        data: { signWheel: data },
      } = await signWheel({ variables: { input: { ids: map(prizes, 'id') } } });

      setStyle({});

      await setTimeout(400, '', { signal: abortController.signal });

      const rouletteItems = document.getElementsByClassName(
        'circle-roulette-item',
      );

      const index = findIndex(
        rouletteItems,
        ({ dataset: { id } }: HTMLElement) =>
          Number(id) === Number(data.prizeId),
      );

      const rotate = 720 * 3 - 30 * index;

      const randomNumbers = [-10, -7, -5, 5, 7, 10];
      const finalRotate = rotate + sample(randomNumbers);

      setStyle({
        transform: `rotate(${finalRotate}deg)`,
        transition: 'transform 15s cubic-bezier(0.63, 0.03, 0.67, 0.98) 0s',
      });

      let prevAngle = 0;

      audioInterval = setInterval(() => {
        const el = document.getElementsByClassName('circle-roulette')[0];
        const st = window.getComputedStyle(el, null);
        const tr =
          st.getPropertyValue('-webkit-transform') ||
          st.getPropertyValue('-moz-transform') ||
          st.getPropertyValue('-ms-transform') ||
          st.getPropertyValue('-o-transform') ||
          st.getPropertyValue('transform') ||
          'fail...';

        if (tr !== 'none') {
          let values: any = tr.split('(')[1];
          values = values.split(')')[0];
          values = values.split(',');

          const a = Number(values[0]);
          const b = Number(values[1]);

          let radians = Math.atan2(b, a);
          if (radians < 0) {
            radians += 2 * Math.PI;
          }

          const angle = Math.round(radians * (180 / Math.PI));

          if (Math.abs(angle - prevAngle) > 30) {
            playAudio(null, 'roulette-start-2.mp3', 0.1);
            prevAngle = angle;
          }
        }
      });

      await setTimeout(15000, '', { signal: abortController.signal });

      clearInterval(audioInterval);
      setActive(false);

      if (data.type === 1) {
        await setTimeout(4500, '', { signal: abortController.signal });

        const getPrize = _.find(prizes, ['id', data?.prizeId]);

        if (getPrize?.showPopup || cookies?.wheelModal) {
          onShow('WheelPrize', {
            image: getPrize.image,
            promoCode: data.value.promo.code,
            endDate: dayjs().add(1, 'day').format(),
          });
        } else {
          navigate('/pay', {
            state: {
              promoCode: data.value.promo.code,
            },
          });
        }
      }

      await refetch();
    } catch (error) {
      if (!abortController.signal.aborted) {
        if (getProfile?.getWheelStep === 2) {
          document.getElementById('vk_subscribe').click();
        } else if (getProfile?.getWheelStep === 5) {
          document.getElementById('senler_subscribe').click();
        }

        setActive(false);

        toast.error(
          <>
            <div className="notify-title">{t('Error')}!</div>
            <div className="notify-text">{t(error.message)}</div>
          </>,
        );
      }
    }
  };

  if (isMobile) {
    setHttpContext({ redirect: '/' });
    return;
  }

  return (
    <>
      <Helmet>
        <title>
          {t('CS GO bonus wheel! GGdrop drum is free!', {
            hostname: getConfig?.hostname,
          })}
        </title>
        <meta
          name="description"
          content={t(
            'Spin every day a free wheel of cs go bonuses! Get top skins and promotional codes! Barban bonuses CSCASE access to everyone immediately after authorization!',
            {
              hostname: getConfig?.hostname,
            },
          )}
        />
      </Helmet>
      <div className="page-circle-game">
        <div
          className={`circle-wrap ${
            getProfile?.getWheelStep === 3 && !isActive && 'play'
          }`}
        >
          <div className="circle-arrow"></div>
          <div className={`circle-game-btn-play ${isActive && 'active'}`}>
            <div className="wheel-pull-lever">{t('wheel_pull_lever')}</div>
            <div
              onMouseDown={onSignWheel}
              className="circle-game-btn pull"
              onMouseEnter={playAudio}
              data-audio="4.wav"
              data-volume="0.5"
            >
              <span className="circle-btn-anim" />
              <div className="circle-btn-arrow" />
            </div>
          </div>
          <div className="circle-roulette" style={style}>
            {prizes.map(item => (
              <div
                className="circle-roulette-item"
                data-id={item.id}
                key={item.id}
              >
                <img src={item.image} alt={item.name} />
              </div>
            ))}
          </div>
          <CircleControls />
        </div>
      </div>

      <div className="page-contracts">
        <Prizes />
      </div>
    </>
  );
};

export default Wheel;
